import React from "react"
import { formatCurrencyString } from "../../utils/format"
import styled from "styled-components"
import { devices } from "../config/breakpoints"
import { setLs } from "../../utils/localStorage"
import { Tag } from "../ui/Tag"

import { useCheckoutState } from "../../checkout/state"
import { Currency } from "../../checkout/types/checkoutTypes"
import { isHeadsetInCart } from "../../checkout/shared/calcStateItems"
import AffirmMessaging from "../../checkout/components/AffirmMessaging"
import FrontrowWidget from "./FrontRow"
import { useSiteConfig } from "../../hooks/useSiteConfig"
import { useProducts } from "../../hooks/useProducts"
import { ProductRelatedPrice } from "./ProductRelatedPrice"
import { Product } from "../../checkout/product"

// TODO: Remove this margin as this component is used in several places and it's not great for the checkout component
// - will need to update other sections in the store to compensate
export const Wrapper = styled.div<{ flex?: string }>`
  display: flex;
  vertical-align: center;
  justify-content: ${({ flex }) => flex ?? "flex-end"};
  margin: 1rem 0;
  @media ${devices.mobilePhone} {
    margin: 0;
  }
`

const CardWrapper = styled.div<{ isCart: boolean }>`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: ${({ isCart }) => (isCart ? "flex-end" : "flex-start")};
`

const PriceDisplayMobileContainer = styled.div<{ hasMargin: boolean }>`
  margin-top: ${({ hasMargin }) => (hasMargin ? "1rem" : "0")};
`

export const Card = styled.div<{ small: boolean }>`
  padding: ${({ small }) => (small ? "0.6rem 0" : "1rem 0")};
  padding-top: 0;
  background-color: var(--color-white);
  display: flex;
  vertical-align: center;
  text-align: left;
  justify-content: start;
`

export const Row = styled.div<{ small: boolean }>`
  display: flex;
  vertical-align: center;
  text-align: left;
  align-items: center;
  justify-content: start;
  flex-wrap: wrap;
  @media ${({ small }) =>
      small ? `(max-width: 1200px)` : devices.mobilePhone} {
    display: block;
  }
`

const Title = styled.div<{ small: boolean }>`
  font-family: var(--font-family-header);
  letter-spacing: 0.5;
  font-weight: 700;
  color: var(--color-black);
  line-height: 1.5em;
  margin-right: 1rem;
  text-align: left;
  font-size: ${({ small }) => (small ? "1rem" : "1.25rem")};
  @media ${devices.mobile} {
    line-height: 1.2rem;
    font-size: 1.1rem;
  }
`

const StyledPrice = styled.div<{ small: boolean; color?: string }>`
  font-family: var(--font-family-header);
  font-weight: 200;
  font-size: 3rem;
  letter-spacing: ${({ small }) => (small ? "-1.5px" : "0.5px")};
  line-height: 3rem;

  color: ${({ color }) => color || "var(--color-teal)"};
  font-size: ${({ small }) => (small ? "2.0rem" : "2.8rem")};
  margin: 0;
  sup {
    text-transform: uppercase;
    vertical-align: super;
    font-size: ${({ small }) => (small ? "1.3rem" : "1.6rem")};
    color: var(--color-black);
    font-weight: 400;
    margin-left: 0.4rem;
  }
  @media ${devices.mobile} {
    margin-bottom: 1rem;
    line-height: 2rem;
    font-size: ${({ small }) => (small ? "1.875rem" : "2rem")};
    sup {
      font-size: ${({ small }) => (small ? "1rem" : "1.1rem")};
    }
  }
`
const StyledPriceMobile = styled.div<{ color?: string }>`
  font-family: var(--font-family-header);
  font-weight: 700;
  font-size: 1rem;
  letter-spacing: 1px;
  color: var(--color-purple);
  sup {
    text-transform: uppercase;
    vertical-align: super;
    font-size: 0.8rem;
    margin-left: 0.2rem;
  }
`

const PriceStrike = styled.div<{ small: boolean }>`
  font-family: var(--font-family-header);
  font-weight: 400;
  letter-spacing: 1px;
  line-height: 1.1em;
  margin-left: ${({ small }) => (small ? "1rem" : "1.75rem")};
  margin-top: 0.5rem;
  max-width: ${({ small }) => (small ? "35%" : "45%")};
  .strike {
    color: var(--color-grey-3);
    text-decoration: line-through;
    margin-right: 1rem;
  }
  span {
    color: var(--color-ultra-violet);
    text-decoration: none;
  }
  @media ${devices.mobile} {
    margin-left: 0;
  }
`

const BillingPeriod = styled.div`
  font-weight: 400;
  color: var(--color-grey-3);
  font-size: 1.1rem;
  line-height: 1.2;
  letter-spacing: 0;
`

const AffirmContainer = styled.div`
  & > div {
    background-color: #f7f8f9;
    border: none;
    padding: 0.5rem 1rem;
  }
`

const ProductPriceDisplay = ({
  priceObj,
  price,
  discount,
  small = false,
  currency,
  priceHeading,
  hidePrice,
  enableAffirmMessaging,
  isMobile,
  countryInventory,
  enableAffirmCanada,
  enableAffirmUS,
  enableFrontRow,
  isCart,
  relatedPrice,
}: {
  priceObj: Product
  price: number
  discount: number
  small?: boolean
  currency: Currency
  priceHeading?: string
  hidePrice?: boolean
  enableAffirmMessaging: boolean
  isMobile?: boolean
  countryInventory: string
  enableAffirmCanada: boolean
  enableAffirmUS: boolean
  enableFrontRow: boolean
  isCart: boolean
  relatedPrice?: Product
}) => {
  const showDiscount = discount !== 0

  return (
    <CardWrapper isCart={isCart}>
      <Card small={small}>
        <div>
          <Title small={small}>{priceHeading}</Title>
          <Row small={small}>
            <StyledPrice small={small} id="product-price">
              <div>
                {formatCurrencyString({
                  value: hidePrice ? 0 : price - discount,
                  currency: currency.toUpperCase(),
                })}

                <sup>{currency.toUpperCase()}</sup>
              </div>
              {!hidePrice && (
                <>
                  {/* {priceObj.hasUpfrontNumCycles() && (
                    <BillingPeriod>
                      {priceObj.getUpfrontNumCycles()}{" "}
                      {priceObj.isMonthly()
                        ? "Months upfront"
                        : "Years upfront"}
                    </BillingPeriod>
                  )} */}

                  {priceObj.isRecurring() &&
                    !priceObj.hasUpfrontNumCycles() && (
                      <BillingPeriod>
                        {priceObj.isMonthly()
                          ? "Billed Monthly"
                          : "Billed Yearly"}
                      </BillingPeriod>
                    )}
                </>
              )}
            </StyledPrice>
            {!showDiscount && relatedPrice && (
              <ProductRelatedPrice
                relatedPrice={{ product: relatedPrice, currency }}
                small={small}
              />
            )}
            {showDiscount && (
              <PriceStrike small={small}>
                <span className="strike">
                  {formatCurrencyString({
                    value: price,
                    currency: currency.toUpperCase(),
                  })}
                </span>
                <Tag
                  color="var(--color-hot-pink)"
                  text={`Save 
              ${formatCurrencyString({
                value: discount,
                currency: currency.toUpperCase(),
              })}`}
                />
              </PriceStrike>
            )}
            {showDiscount && relatedPrice && (
              <ProductRelatedPrice
                relatedPrice={{ product: relatedPrice, currency }}
                small={small}
                forceNewLine={true}
              />
            )}
          </Row>
        </div>
      </Card>

      {enableAffirmMessaging && !isMobile && !priceObj.isRecurring() && (
        <AffirmContainer>
          <AffirmMessaging
            total={price}
            currency={currency}
            country={countryInventory}
            enableAffirmCanada={enableAffirmCanada}
            enableAffirmUS={enableAffirmUS}
          />
        </AffirmContainer>
      )}
      {enableFrontRow && <FrontrowWidget />}
    </CardWrapper>
  )
}

const ProductPriceDisplayMobile = ({
  price,
  discount,
  currency,
  hidePrice,
}: {
  price: number
  discount: number
  currency: Currency
  hidePrice?: boolean
}) => {
  return (
    <>
      <StyledPriceMobile>
        {formatCurrencyString({
          value: hidePrice ? 0 : price - discount,
          currency: currency.toUpperCase(),
        })}

        <sup>{currency.toUpperCase()}</sup>
      </StyledPriceMobile>
    </>
  )
}

const ProductPrice = ({
  priceObj,
  small = false,
  isMobile,
  isCart,
  hideFrontRow,
  hideAffirm,
  hideRelatedProducts,
  flex,
}: {
  priceObj: Product
  small?: boolean
  isMobile?: boolean
  isCart?: boolean
  hideFrontRow?: boolean
  hideAffirm?: boolean
  hideRelatedProducts?: boolean
  flex?: string
}) => {
  if (!priceObj) {
    return <></>
  }

  const siteConfig = useSiteConfig()
  const {
    enableAffirmMessaging,
    enableAffirmCanada,
    enableAffirmUS,
    enableFrontRow,
  } = siteConfig

  const checkoutState = useCheckoutState()
  const {
    currency,
    cartItems,
    couponDiscountDays,
    countryInventory,
  } = checkoutState

  const products = useProducts()

  const relatedPriceProd =
    isCart || hideRelatedProducts
      ? undefined
      : priceObj.getRelatedProduct(products, countryInventory)

  const isBundledMembershipItem =
    priceObj.isSubscriptionWithTrialDays() && isHeadsetInCart(cartItems)

  setLs("pt_currency", { value: currency })

  let discount = priceObj.USD_Discount
  const isBilledMonthly = priceObj.isMonthly()
  const isBilledYearly = priceObj.isYearly()
  if (currency === "cad") {
    discount = priceObj.CAD_Discount
  }

  // If we have a Stripe price, use it rather than the price from Strapi
  let price = priceObj.getPriceAmt(currency, true, isCart)
  if (price === null) {
    return <></>
  }

  setLs("pt_price", { value: price })
  setLs("pt_discount", { value: discount })

  const hidePrice =
    (isBilledYearly || isBilledMonthly) &&
    (isBundledMembershipItem || couponDiscountDays > 0)

  return (
    <>
      <Wrapper flex={flex}>
        {!isMobile && (
          <ProductPriceDisplay
            priceObj={priceObj}
            price={price}
            discount={discount}
            currency={currency}
            small={small}
            priceHeading={priceObj.priceHeading}
            hidePrice={hidePrice}
            enableAffirmMessaging={
              !hideAffirm && enableAffirmMessaging && priceObj.isHeadset()
            }
            isMobile={isMobile}
            countryInventory={countryInventory}
            enableAffirmCanada={enableAffirmCanada}
            enableAffirmUS={enableAffirmUS}
            enableFrontRow={
              enableFrontRow && !hideFrontRow && priceObj.isHeadset()
            }
            isCart={isCart || false}
            relatedPrice={relatedPriceProd}
          />
        )}
        {isMobile && (
          <PriceDisplayMobileContainer hasMargin={true}>
            <ProductPriceDisplayMobile
              price={price}
              discount={discount}
              currency={currency}
              hidePrice={hidePrice}
            />
          </PriceDisplayMobileContainer>
        )}
      </Wrapper>
      {!hideAffirm &&
        enableAffirmMessaging &&
        isMobile &&
        !isBilledMonthly &&
        !isBilledYearly && (
          <div style={{ paddingTop: "2rem" }}>
            <AffirmContainer>
              <AffirmMessaging
                total={price}
                currency={currency}
                country={countryInventory}
                enableAffirmCanada={enableAffirmCanada}
                enableAffirmUS={enableAffirmUS}
              />
            </AffirmContainer>
          </div>
        )}
    </>
  )
}

export default ProductPrice
